import React, { useEffect, useState } from 'react';
import ReactFlow, { 
  Background, 
  Controls, 
  MiniMap,
  MarkerType,
  useNodesState,
  useEdgesState
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';

// Custom node styles
const nodeStyles = {
  start: 'bg-green-100 border-green-500',
  end: 'bg-red-100 border-red-500',
  process: 'bg-blue-100 border-blue-500',
  intent: 'bg-purple-100 border-purple-500',
  tool: 'bg-yellow-100 border-yellow-500'
};

// Get node type based on id or name
const getNodeType = (id, name) => {
  if (id === '__start__') return 'start';
  if (id === '__end__') return 'end';
  if (id.includes('intent')) return 'intent';
  if (id.includes('tool')) return 'tool';
  return 'process';
};

// Layout the graph using dagre
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  
  // Set graph direction
  dagreGraph.setGraph({ rankdir: direction });

  // Add nodes with dimensions
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 180, height: 60 });
  });

  // Add edges
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply calculated positions to nodes
  return {
    nodes: nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - 90, // Center the node
          y: nodeWithPosition.y - 30,
        }
      };
    }),
    edges
  };
};

const WorkflowGraph = ({ graphData }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    if (!graphData) return;
    
    // Transform backend nodes to ReactFlow format
    const flowNodes = Object.entries(graphData.nodes).map(([id, nodeData]) => {
      const nodeType = getNodeType(nodeData.id, nodeData.name);
      
      return {
        id: nodeData.id,
        data: { 
          label: (
            <div className="font-medium">
              <div>{nodeData.name}</div>
              {nodeData.data && (
                <div className="text-xs text-gray-500 mt-1">
                 {typeof nodeData.data === 'object' 
                    ? nodeData.data.type || JSON.stringify(nodeData.data)
                    : String(nodeData.data).split('(')[0]}
                </div>
              )}
            </div>
          )
        },
        position: { x: 0, y: 0 }, // Will be calculated by layout algorithm
        className: `border-2 rounded-md shadow-md p-3 ${nodeStyles[nodeType]}`,
      };
    });
    
    // Transform backend edges to ReactFlow format
    const flowEdges = graphData.edges.map((edge, index) => {
      return {
        id: `e${index}`,
        source: edge.source,
        target: edge.target,
        label: edge.data,
        animated: edge.conditional,
        labelStyle: { fontSize: '10px', fill: '#666', fontWeight: 500 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
        },
        style: {
          stroke: edge.conditional ? '#8b5cf6' : '#64748b',
          strokeWidth: 2,
        },
      };
    });
    
    // Apply layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      flowNodes, 
      flowEdges
    );
    
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [graphData, setNodes, setEdges]);

  return (
    <div className="w-full h-[600px] border border-gray-200 rounded-lg shadow-sm bg-white">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
      >
        <Controls className="bg-white shadow-md rounded-md border border-gray-200" />
        <MiniMap 
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="bg-white shadow-md rounded-md border border-gray-200"
        />
        <Background variant="dots" gap={12} size={1} color="#f1f5f9" />
      </ReactFlow>
    </div>
  );
};

export default WorkflowGraph;